import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { addMinutes, isPast, setMilliseconds } from 'date-fns';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { ignoreTimezone } from '@order/utils';

import { notifee } from '../../integrations';
import {
  ORDER_FEEDBACK_NOTIFICATION_ANDROID_COLOR,
  ORDER_FEEDBACK_NOTIFICATION_ANDROID_ICON_NAME,
  ORDER_FEEDBACK_NOTIFICATION_PRESS_ACTION_ID,
  ORDER_FEEDBACK_NOTIFICATION_TIMESTAMP_TYPE,
  ORDER_FEEDBACK_NOTIFICATIONS_CHANNEL_ID,
} from './useOrderFeedbackNotifications.constants';
import {
  checkIfOrderFeedbackNotificationsEnabled,
  getOrderFeedbackNotificationId,
} from './useOrderFeedbackNotifications.utils';

// ─────────────────────────────────────────────────────────────────────────────

export const useOrderFeedbackNotifications = () => {
  const { formatMessage } = useIntl();

  // ─── Flags ───────────────────────────────────────────────────────────

  const isOrderFeedbackNotificationsEnabled = useFeatureFlag('CELS-4251-order-feedback-notifications-enabled'); // prettier-ignore
  const orderFeedbackNotificationDelay = useFeatureFlag('CELS-4251-order-feedback-notification-delay'); // prettier-ignore

  // ─── Helpers ─────────────────────────────────────────────────────────

  /**
   * Creates a local push notification (native only) to collect feedback from the customer,
   * after three hours of the order's wanted time.
   */
  const scheduleOrderFeedbackNotification = useCallback<ScheduleOrderFeedbackNotification>(
    async (props) => {
      if (!checkIfOrderFeedbackNotificationsEnabled()) return;

      const { orderId, wantedTime } = props;

      const orderWantedTime = ignoreTimezone(wantedTime);
      const hasValidWantedTime = Boolean(orderWantedTime && !isPast(orderWantedTime)); // prettier-ignore

      const shouldCreateNotification =
        orderWantedTime && isOrderFeedbackNotificationsEnabled && hasValidWantedTime;

      if (!shouldCreateNotification) return;

      const id = getOrderFeedbackNotificationId(orderId);

      const notificationDate = addMinutes(orderWantedTime, orderFeedbackNotificationDelay); // prettier-ignore
      const triggerTimestamp = setMilliseconds(notificationDate, 0).getTime();

      const title = formatMessage(messages.orderFeedbackNotificationTitle);
      const body = formatMessage(messages.orderFeedbackNotificationBody);

      try {
        await notifee?.createTriggerNotification(
          {
            id,
            title,
            body,
            android: {
              channelId: ORDER_FEEDBACK_NOTIFICATIONS_CHANNEL_ID,
              smallIcon: ORDER_FEEDBACK_NOTIFICATION_ANDROID_ICON_NAME,
              color: ORDER_FEEDBACK_NOTIFICATION_ANDROID_COLOR,
              pressAction: {
                id: ORDER_FEEDBACK_NOTIFICATION_PRESS_ACTION_ID,
              },
            },
            data: { orderId },
          },
          {
            type: ORDER_FEEDBACK_NOTIFICATION_TIMESTAMP_TYPE,
            timestamp: triggerTimestamp,
          },
        );
      } catch {}
    },
    [formatMessage, isOrderFeedbackNotificationsEnabled, orderFeedbackNotificationDelay],
  );

  /**
   * Cancels the local push notification for order feedback, based on the order ID (native only).
   */
  const cancelScheduledOrderFeedbackNotification = useCallback<CancelOrderFeedbackNotification>(
    async (orderId: string) => {
      if (!isOrderFeedbackNotificationsEnabled || !checkIfOrderFeedbackNotificationsEnabled())
        return;

      await notifee?.cancelTriggerNotification(getOrderFeedbackNotificationId(orderId));
    },
    [isOrderFeedbackNotificationsEnabled],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    scheduleOrderFeedbackNotification,
    cancelScheduledOrderFeedbackNotification,
  };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  orderFeedbackNotificationTitle: {
    defaultMessage: "How'd that go?",
    description: 'Order Feedback Notification | Title',
  },
  orderFeedbackNotificationBody: {
    defaultMessage: 'Rate your order so we can do even better next time.',
    description: 'Order Feedback Notification | Body',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ScheduleOrderFeedbackNotification = (
  input: Readonly<{
    orderId: string;
    wantedTime: string;
  }>,
) => Promise<void>;

type CancelOrderFeedbackNotification = (orderId: string) => Promise<void>;
